import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Upload as UploadIcon } from "lucide-react";

export default function Upload() {
  const [title, setTitle] = useState("");
  const [bpm, setBpm] = useState("");
  const [musicalKey, setMusicalKey] = useState("");
  const [genre, setGenre] = useState("");
  const [audio, setAudio] = useState(null);
  const [genCover, setGenCover] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const nav = useNavigate();

  const handle = async (e) => {
    e.preventDefault();
    if (!audio) {
      toast.error("Pick an audio file first");
      return;
    }
    setSubmitting(true);
    const fd = new FormData();
    fd.append("title", title);
    if (bpm) fd.append("bpm", bpm);
    if (musicalKey) fd.append("musical_key", musicalKey);
    if (genre) fd.append("genre", genre);
    fd.append("audio", audio);
    try {
      const { data } = await api.post("/beats", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Beat uploaded");
      if (genCover) {
        toast.message("Generating cover art...");
        try {
          await api.post(`/beats/${data.id}/cover`);
        } catch {
          toast.error("Cover generation failed (you can retry later)");
        }
      }
      nav("/feed");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="upload-page" className="px-6 md:px-12 py-12 max-w-3xl mx-auto">
      <div className="font-mono-acc text-xs text-secondary">// DROP A BEAT</div>
      <h1 className="font-display text-5xl md:text-6xl mt-3">New <span className="text-primary">upload</span>.</h1>
      <p className="text-muted-foreground mt-3 max-w-xl">mp3 or wav up to 25MB. Add metadata so judges know what they're hearing.</p>

      <form onSubmit={handle} className="mt-10 border border-border bg-card p-8 space-y-5">
        <label className="block">
          <span className="font-mono-acc text-[10px] text-muted-foreground">Title *</span>
          <input
            data-testid="upload-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
            maxLength={80}
            className="mt-1 w-full bg-input border border-border px-3 py-3 outline-none focus:border-primary"
          />
        </label>
        <div className="grid sm:grid-cols-3 gap-4">
          <label className="block">
            <span className="font-mono-acc text-[10px] text-muted-foreground">BPM</span>
            <input
              data-testid="upload-bpm"
              type="number"
              min="40"
              max="240"
              value={bpm}
              onChange={(e) => setBpm(e.target.value)}
              className="mt-1 w-full bg-input border border-border px-3 py-3 outline-none focus:border-primary"
            />
          </label>
          <label className="block">
            <span className="font-mono-acc text-[10px] text-muted-foreground">Key</span>
            <input
              data-testid="upload-key"
              placeholder="A min"
              value={musicalKey}
              onChange={(e) => setMusicalKey(e.target.value)}
              className="mt-1 w-full bg-input border border-border px-3 py-3 outline-none focus:border-primary"
            />
          </label>
          <label className="block">
            <span className="font-mono-acc text-[10px] text-muted-foreground">Genre</span>
            <input
              data-testid="upload-genre"
              placeholder="trap, lo-fi, drill..."
              value={genre}
              onChange={(e) => setGenre(e.target.value)}
              className="mt-1 w-full bg-input border border-border px-3 py-3 outline-none focus:border-primary"
            />
          </label>
        </div>
        <label className="block">
          <span className="font-mono-acc text-[10px] text-muted-foreground">Audio file *</span>
          <input
            data-testid="upload-file"
            type="file"
            accept="audio/*,.mp3,.wav"
            onChange={(e) => setAudio(e.target.files?.[0] || null)}
            required
            className="mt-1 w-full bg-input border border-border px-3 py-3 file:bg-primary file:text-white file:border-0 file:px-3 file:py-1 file:mr-3 file:font-mono-acc file:text-xs"
          />
        </label>
        <label className="flex items-center gap-3 border border-border p-3 cursor-pointer hover:border-secondary">
          <input
            data-testid="upload-gen-cover"
            type="checkbox"
            checked={genCover}
            onChange={(e) => setGenCover(e.target.checked)}
            className="w-4 h-4 accent-secondary"
          />
          <Sparkles size={14} className="text-secondary" />
          <span className="font-mono-acc text-xs">Auto-generate AI cover art</span>
        </label>
        <button
          data-testid="upload-submit"
          disabled={submitting}
          className="w-full border border-primary bg-primary text-white py-3 font-mono-acc text-xs hover:bg-white hover:text-primary disabled:opacity-50 inline-flex items-center justify-center gap-2"
        >
          <UploadIcon size={14} />
          {submitting ? "Uploading..." : "Upload Beat"}
        </button>
      </form>
    </div>
  );
}
